import { useState } from 'react';
import { UI_TEXT } from './utils/constants.js';
import './ShareButton.css';
import { useTournamentState } from './TournamentContext.jsx';

/**
 * ShareButton - Share tournament results via Web Share API or social links
 * @param {Object} props
 * @param {Object} props.champion - Winner candidate object
 * @param {string} props.language - Language code ('ko' or 'en')
 */
export default function ShareButton({ champion, language = 'ko' }) {
  const state = useTournamentState();
  const [showOptions, setShowOptions] = useState(false);
  const [copied, setCopied] = useState(false);

  const text = UI_TEXT[language] || UI_TEXT.ko;
  const championName = language === 'en' ? champion.nameEn || champion.name : champion.name;
  const shareText = text.shareMessage(championName);
  const shareUrl = window.location.href;

  const handleShare = async () => {
    // Try Web Share API first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: text.shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        // User cancelled or error occurred
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
          setShowOptions(true);
        }
      }
    } else {
      // Fallback: show share options
      setShowOptions(!showOptions);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleTwitterShare = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      shareText
    )}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twitterUrl, '_blank', 'width=550,height=420');
  };

  const handleFacebookShare = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      shareUrl
    )}`;
    window.open(facebookUrl, '_blank', 'width=550,height=420');
  };

  return (
    <div
      className="share-button"
    >
      <button
        className="share-button__main"
        onClick={handleShare}
        type="button"
        aria-label={text.share}
        style={{ border: `1px solid ${state.themeColor}`, borderRadius: '8px' }}
      >
        {text.share}
      </button>

      {showOptions && (
        <div className="share-button__options">
          <button
            className="share-button__option"
            onClick={handleTwitterShare}
            type="button"
            aria-label={text.twitter}
          >
            {text.twitter}
          </button>
          <button
            className="share-button__option"
            onClick={handleFacebookShare}
            type="button"
            aria-label={text.facebook}
          >
            {text.facebook}
          </button>
          <button
            className="share-button__option"
            onClick={handleCopyLink}
            type="button"
            aria-label={text.copyLink}
          >
            {copied ? text.copied : text.copyLink}
          </button>
        </div>
      )}
    </div>
  );
}